import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, RotateCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import CountdownBanner from "@/components/CountdownBanner";
import Reveal from "@/components/Reveal";
import GoldParticles from "@/components/GoldParticles";
import { SpadeMark } from "@/components/brand";

const indInitial = { name: "", email: "", phone: "", college: "", degree_year: "", event: "", id_reference: "", accommodation: "" };
const grpInitial = { team_name: "", college: "", leader_name: "", leader_email: "", leader_phone: "", delegates: 2, events: [], faculty_coordinator: "" };

function Field({ label, children }) {
  return (
    <div>
      <label className="label-lux">{label}</label>
      {children}
    </div>
  );
}

function DelegatePass({ result, onReset }) {
  const isInd = result.type === "individual";
  const title = isInd ? result.name : result.team_name;
  const events = isInd ? [result.event] : result.events || [];
  return (
    <Reveal>
      <div data-testid="reg-pass-card" className="relative mx-auto max-w-lg overflow-hidden border border-[#D4AF37]/50 bg-[#0F0F14]">
        <div className="absolute inset-0 vignette" aria-hidden="true" />
        <span className="absolute -right-6 -top-8 text-[11rem] leading-none text-[#D4AF37]/8 select-none" aria-hidden="true">♠</span>
        <div className="relative border-b border-[#D4AF37]/25 px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SpadeMark className="h-7 w-7" />
            <span className="font-bebas text-2xl tracking-[0.22em] text-[#F6F4EE]">FINATEX<span className="text-[#D4AF37]">'26</span></span>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B39343]">Delegate Pass</span>
        </div>
        <div className="relative px-8 py-8">
          <p className="label-lux">{isInd ? "Delegate" : "Group"}</p>
          <p data-testid="reg-pass-name" className="font-display text-2xl sm:text-3xl font-bold text-[#F6F4EE]">{title}</p>
          <p className="mt-1 text-sm text-[#A19E95]">{result.college}</p>
          <div className="mt-6 grid grid-cols-2 gap-5">
            <div>
              <p className="label-lux mb-1">Token ID</p>
              <p data-testid="reg-pass-token" className="font-mono text-sm tracking-[0.15em] text-[#E5C97A]">{result.token_id}</p>
            </div>
            <div>
              <p className="label-lux mb-1">Entry</p>
              <p className="font-mono text-sm tracking-[0.15em] text-[#E5C97A]">{isInd ? "INDIVIDUAL" : `TEAM OF ${result.delegates}`}</p>
            </div>
          </div>
          <div className="mt-6">
            <p className="label-lux mb-2">{isInd ? "Event" : "Events"}</p>
            <div className="flex flex-wrap gap-2">
              {events.map((ev) => (
                <span key={ev} className="border border-[#D4AF37]/30 px-3 py-1.5 text-[11px] tracking-[0.1em] text-[#F6F4EE]/85">{ev}</span>
              ))}
            </div>
          </div>
          <p className="mt-8 border-t border-dashed border-[#D4AF37]/25 pt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#6E6B63]">
            20–21 Nov 2026 · CHRIST (DU), Pune Lavasa · Present this pass at the Welcome Desk
          </p>
        </div>
      </div>
      <div className="mt-10 text-center">
        <p className="text-sm text-[#A19E95]">A confirmation has been recorded with the organising committee.</p>
        <button
          onClick={onReset}
          data-testid="reg-another-btn"
          className="group mt-6 inline-flex items-center gap-3 border border-[#D4AF37]/60 px-8 py-4 text-xs uppercase tracking-[0.22em] text-[#D4AF37] transition-all duration-300 hover:bg-[#D4AF37] hover:text-[#08080A]"
        >
          <RotateCcw size={14} />
          Register Another Entry
        </button>
      </div>
    </Reveal>
  );
}

export default function Register() {
  const [params] = useSearchParams();
  const preEvent = params.get("event") || "";
  const [mode, setMode] = useState("individual");
  const [events, setEvents] = useState([]);
  const [ind, setInd] = useState({ ...indInitial, event: preEvent });
  const [grp, setGrp] = useState({ ...grpInitial, events: preEvent ? [preEvent] : [] });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const eventTitles = useMemo(
    () => events.filter((e) => e.status === "Open" || e.status === "Upcoming").map((e) => e.name),
    [events]
  );

  const setIndField = (k) => (e) => setInd({ ...ind, [k]: e.target.value });
  const setGrpField = (k) => (e) => setGrp({ ...grp, [k]: e.target.value });
  const toggleGrpEvent = (title) =>
    setGrp((g) => ({ ...g, events: g.events.includes(title) ? g.events.filter((t) => t !== title) : [...g.events, title] }));

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "individual") {
      const missing = ["name", "email", "phone", "college", "degree_year", "event"].filter((k) => !String(ind[k]).trim());
      if (missing.length) {
        toast.error("Please complete all required fields.");
        return;
      }
    } else {
      if (!grp.team_name.trim() || !grp.college.trim() || !grp.leader_name.trim() || !grp.leader_email.trim() || !grp.leader_phone.trim()) {
        toast.error("Please complete all required fields.");
        return;
      }
      if (!grp.events.length) {
        toast.error("Select at least one event for your group.");
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = mode === "individual" ? { type: "individual", ...ind } : { type: "group", ...grp, delegates: Number(grp.delegates) };
      const { data } = await api.post("/registrations", payload);
      setResult(data);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setResult(null);
    setInd(indInitial);
    setGrp(grpInitial);
  };

  return (
    <div data-testid="register-page">
      <CountdownBanner />
      <section className="relative overflow-hidden border-b border-white/5 py-24 sm:py-28">
        <GoldParticles density={35} />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow mb-5">Registration</p>
            <h1 data-testid="register-heading" className="max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight text-[#F6F4EE]">
              Buy In.<br />
              <span className="text-gold-sheen">All Bets On You.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-sm sm:text-base leading-relaxed text-[#A19E95]">
              Register as an individual delegate or enter your college group. No account needed — your delegate
              pass is issued the moment your entry is confirmed.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 sm:px-8 py-20 sm:py-24">
        {result ? (
          <DelegatePass result={result} onReset={reset} />
        ) : (
          <>
            <Reveal>
              <div className="grid grid-cols-2 border border-white/10" data-testid="reg-type-toggle">
                {[
                  { key: "individual", label: "Individual", sub: "One delegate" },
                  { key: "group", label: "Group", sub: "One college, one team" },
                ].map((m) => (
                  <button
                    key={m.key}
                    data-testid={`reg-type-${m.key}`}
                    onClick={() => setMode(m.key)}
                    className={`px-6 py-5 text-left transition-colors duration-300 ${
                      mode === m.key ? "bg-[#D4AF37] text-[#08080A]" : "bg-[#0F0F14] text-[#A19E95] hover:text-[#F6F4EE]"
                    }`}
                  >
                    <span className="block font-display text-lg font-semibold">{m.label}</span>
                    <span className={`mt-1 block text-[11px] uppercase tracking-[0.18em] ${mode === m.key ? "text-[#08080A]/70" : "text-[#6E6B63]"}`}>
                      {m.sub}
                    </span>
                  </button>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <form onSubmit={submit} data-testid="reg-form" className="mt-10 border border-white/8 bg-[#0F0F14] p-8 sm:p-10">
                {mode === "individual" ? (
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Full Name *">
                      <input data-testid="reg-ind-name" className="input-lux" value={ind.name} onChange={setIndField("name")} placeholder="Delegate's full name" />
                    </Field>
                    <Field label="Email Address *">
                      <input type="email" data-testid="reg-ind-email" className="input-lux" value={ind.email} onChange={setIndField("email")} placeholder="you@college.edu" />
                    </Field>
                    <Field label="Contact Number *">
                      <input data-testid="reg-ind-phone" className="input-lux" value={ind.phone} onChange={setIndField("phone")} placeholder="+91 ..." />
                    </Field>
                    <Field label="College / University *">
                      <input data-testid="reg-ind-college" className="input-lux" value={ind.college} onChange={setIndField("college")} placeholder="Institution name" />
                    </Field>
                    <Field label="Degree & Year *">
                      <input data-testid="reg-ind-degree" className="input-lux" value={ind.degree_year} onChange={setIndField("degree_year")} placeholder="e.g., B.Com Hons, 3rd Year" />
                    </Field>
                    <Field label="Event Selection *">
                      <select data-testid="reg-ind-event" className="input-lux" value={ind.event} onChange={setIndField("event")}>
                        <option value="">Choose your event</option>
                        {eventTitles.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="College ID Reference No.">
                      <input data-testid="reg-ind-idref" className="input-lux" value={ind.id_reference} onChange={setIndField("id_reference")} placeholder="Optional — ID card number" />
                    </Field>
                    <Field label="Dietary / Accommodation Request">
                      <input data-testid="reg-ind-accommodation" className="input-lux" value={ind.accommodation} onChange={setIndField("accommodation")} placeholder="Optional" />
                    </Field>
                  </div>
                ) : (
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Team / Group Name *">
                      <input data-testid="reg-grp-team" className="input-lux" value={grp.team_name} onChange={setGrpField("team_name")} placeholder="e.g., Fergusson Finance Society" />
                    </Field>
                    <Field label="College / University *">
                      <input data-testid="reg-grp-college" className="input-lux" value={grp.college} onChange={setGrpField("college")} placeholder="Institution name" />
                    </Field>
                    <Field label="Group Leader Name *">
                      <input data-testid="reg-grp-leader" className="input-lux" value={grp.leader_name} onChange={setGrpField("leader_name")} placeholder="Leader's full name" />
                    </Field>
                    <Field label="Leader Email *">
                      <input type="email" data-testid="reg-grp-email" className="input-lux" value={grp.leader_email} onChange={setGrpField("leader_email")} placeholder="leader@college.edu" />
                    </Field>
                    <Field label="Leader Phone *">
                      <input data-testid="reg-grp-phone" className="input-lux" value={grp.leader_phone} onChange={setGrpField("leader_phone")} placeholder="+91 ..." />
                    </Field>
                    <Field label="Number of Delegates (2–12) *">
                      <input type="number" min={2} max={12} data-testid="reg-grp-delegates" className="input-lux" value={grp.delegates} onChange={setGrpField("delegates")} />
                    </Field>
                    <Field label="Faculty Coordinator (Optional)">
                      <input data-testid="reg-grp-faculty" className="input-lux" value={grp.faculty_coordinator} onChange={setGrpField("faculty_coordinator")} placeholder="Faculty coordinator name" />
                    </Field>
                    <div className="sm:col-span-2">
                      <label className="label-lux">Events Participating *</label>
                      <div className="grid gap-2 sm:grid-cols-2" data-testid="reg-grp-events">
                        {eventTitles.map((t) => (
                          <button
                            type="button"
                            key={t}
                            data-testid={`reg-grp-event-${t.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                            onClick={() => toggleGrpEvent(t)}
                            className={`border px-4 py-3 text-left text-xs tracking-[0.06em] transition-all duration-300 ${
                              grp.events.includes(t)
                                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#E5C97A]"
                                : "border-white/10 text-[#A19E95] hover:border-[#D4AF37]/40 hover:text-[#F6F4EE]"
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="reg-submit-btn"
                  className="group mt-10 inline-flex w-full items-center justify-center gap-3 bg-[#D4AF37] px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-[#08080A] transition-colors duration-300 hover:bg-[#E5C97A] disabled:opacity-50"
                >
                  {submitting ? "Dealing Your Pass..." : "Confirm Registration"}
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <p className="mt-5 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-[#6E6B63]">
                  Entries are confirmed instantly · The committee will reach out with further details
                </p>
              </form>
            </Reveal>
          </>
        )}
      </section>
    </div>
  );
}
